import {
    Component,
    computed,
    DestroyRef,
    EventEmitter,
    inject,
    input,
    Input,
    OnChanges,
    OnInit,
    Output,
    signal,
    SimpleChanges,
    WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { DataMartField, DataMartPicklistValue, DataMartTable, PicklistElement } from '@r1-compass-library/models';
import { lookupComparators } from '@r1-compass-library/models/content-library/content-library.model';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { TypeaheadSearchComponent } from '@r1-compass-library/components';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCalendar, faLightbulb, faTable, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { MatNativeDateModule } from '@angular/material/core';
import { GenericSelectionCriterionForm } from '@r1-compass-library/models/criteria/selection-criteria-form.model';
import { DataMartService } from '@r1-compass-library/services';
import { filter, map, startWith, take, tap } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filterNil } from 'ngxtension/filter-nil';
import { nonEmptyArrayValidator } from '@r1-compass-library/validators/nonEmptyArray.validator';
import { typeValidator } from '@r1-compass-library/validators';

@Component({
    selector: 'r1-compass-app-generic-selection-criterion',
    imports: [
        FontAwesomeModule,
        FormsModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatInputModule,
        MatNativeDateModule,
        MatSelectModule,
        ReactiveFormsModule,
        TypeaheadSearchComponent,
    ],
    templateUrl: './generic-selection-criterion.component.html',
    styleUrl: './generic-selection-criterion.component.scss',
})
export class GenericSelectionCriterionComponent implements OnInit, OnChanges {
    readonly #dataMartService = inject(DataMartService);
    readonly #destroyRef = inject(DestroyRef);

    @Input()
    criterionForm!: FormGroup<GenericSelectionCriterionForm>;

    requiredTableName = input<string | undefined>(undefined);
    requiredDatamartFieldName = input<string | undefined>(undefined);
    deletable = input<boolean>(true);
    displayOnly = input<boolean>(false);
    displayIndex = input<boolean>(true);
    index = input.required<number>();
    required = input.required<boolean>();

    tabClass = computed(() => (this.required() ? 'criterion-index-tab required-row-tab' : 'criterion-index-tab'));

    @Output()
    deleteCriterion: EventEmitter<number> = new EventEmitter<number>();

    private matchingField = computed(() => {
        const tableName = this.criterionFormValue().tableName;
        const selectedTable = this.tables()?.find(table => table.name === tableName);
        const selectedFieldName = this.criterionFormValue().dataMartFieldName;
        const matchingField = selectedTable?.fields.find(field => field.name === selectedFieldName);
        return matchingField;
    });
    comparators = computed(() => {
        const tableName = this.criterionFormValue().tableName;
        const selectedTable = this.tables()?.find(table => table.name === tableName);
        const selectedFieldName = this.criterionFormValue().dataMartFieldName;
        const matchingField = selectedTable?.fields.find(field => field.name === selectedFieldName);
        return matchingField ? lookupComparators(matchingField) : [];
    });
    selectedComparatorValueType = computed(() => {
        const type = this.comparators().find(
            comparator => comparator.value === this.criterionFormValue().comparator
        )?.valueType;
        return type ?? 'nothing';
    });
    type = computed(() => {
        return this.criterionFormValue().type === 'generic' ? 'generic' : 'Model';
    });
    badgeClass = computed(() => (this.type() === 'Model' ? 'criterion-type-badge model' : 'criterion-type-badge'));
    badgeIcon = computed(() => (this.type() === 'Model' ? faLightbulb : faTable));

    picklistOptions: DataMartPicklistValue[] = [];
    timeIntervalOptions: Omit<PicklistElement, 'id'>[] = [
        {
            name: 'Days',
            value: 'Days',
        },
        {
            name: 'Weeks',
            value: 'weeks',
        },
        {
            name: 'Months',
            value: 'months',
        },
        {
            name: 'Years',
            value: 'years',
        },
    ];

    multipickFormControl = new FormControl<string[]>([], { nonNullable: true, validators: [nonEmptyArrayValidator()] });

    faTrash = faTrash;
    faCalendar = faCalendar;

    valueTypeaheadResults: { value: string; display: string }[] = [];
    private typeValidator1: ValidatorFn | null = null;
    private typeValidator2: ValidatorFn | null = null;

    constructor() {
        const library = inject(FaIconLibrary);

        library.addIcons(faTrash, faCalendar, faLightbulb, faTable);
    }

    tables = input.required<DataMartTable[] | undefined>();
    sortedTables = computed(() => {
        return this.tables()?.sort(function (a, b) {
            return a.displayName.localeCompare(b.displayName);
        });
    });
    tables$ = toObservable(this.tables);

    fields: WritableSignal<DataMartField[]> = signal([]);
    sortedFields = computed(() => {
        return this.fields().sort((a, b) => {
            return a.displayName.localeCompare(b.displayName);
        });
    });

    criterionFormValue = signal(this.criterionForm?.getRawValue());

    ngOnInit(): void {
        // Michael's personal Stackblitz has an example of doing this in a cleaner way but can't find it now. This is fine.
        // Sets the criterion form value
        this.criterionForm?.valueChanges
            .pipe(
                startWith(this.criterionForm.getRawValue()),
                map(() => this.criterionForm.getRawValue()),
                tap(res => {
                    this.criterionFormValue.set(res);
                }),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe();

        this.tables$
            .pipe(
                filterNil(),
                filter(tables => tables.length > 0),
                take(1)
            )
            .subscribe(() => this.updateFormControlsForFormLoad());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['criterionForm']?.firstChange) {
            this.criterionFormValue.set(this.criterionForm.getRawValue());
            this.updateFormControlsForFormLoad();
        }
    }

    private updateFormControlsForFormLoad() {
        this.setRequiredTableAndField();
        const tableName = this.criterionFormValue().tableName;
        if (tableName) {
            this.onTableSelect(tableName, true);
        }
        const matchingField = this.matchingField();
        if (matchingField) {
            this.onFieldSelect(matchingField, true);
        }
        this.onComparatorSelect(true);
        this.populateMultipicklistControl();
        if (this.displayOnly()) {
            this.disableAllControls();
        }
    }

    private setRequiredTableAndField() {
        if (this.requiredTableName()) {
            this.criterionForm.controls.tableName.setValue(this.requiredTableName());
            this.criterionForm.controls.tableName.disable();
        }
        if (this.requiredDatamartFieldName()) {
            this.criterionForm.controls.dataMartFieldName.setValue(this.requiredDatamartFieldName());
            this.criterionForm.controls.dataMartFieldName.disable();
        }
    }

    onFieldSelect(field: DataMartField, onLoad?: boolean) {
        if (field.fieldType.toLowerCase() === 'picklist' || field.fieldType.toLowerCase() === 'multipicklist') {
            this.setPicklistOptions();
        } else {
            this.picklistOptions = []; //if we move away from picklist type we should clear the values so they aren't stale
        }
        if (!onLoad) {
            this.criterionForm.controls.not.reset();
            this.criterionForm.controls.comparator.reset();
            this.criterionForm.controls.value1.reset();
            this.criterionForm.controls.value2.reset();
            this.multipickFormControl.reset();
        }
    }

    onComparatorSelect(onLoad?: boolean) {
        if (!onLoad) {
            this.multipickFormControl.reset();
            this.criterionForm?.controls.value1.reset();
            this.criterionForm?.controls.value2.reset();
        }

        const comparatorType = this.selectedComparatorValueType();

        // Enable/disable based on comparator type
        if (comparatorType === 'date&date' || comparatorType === 'text&text' || comparatorType === 'timeInterval') {
            this.criterionForm?.controls.value1.enable();
            this.criterionForm.controls.value2.enable();
        } else if (comparatorType === 'nothing') {
            this.criterionForm?.controls.value1.disable();
            this.criterionForm?.controls.value2.disable();
        } else {
            this.criterionForm?.controls.value1.enable();
            this.criterionForm.controls.value2.disable();
        }

        // Add validators based on field data type and comparator type
        if (this.typeValidator1) {
            this.criterionForm.controls.value1.controls.value.removeValidators(this.typeValidator1);
            this.typeValidator1 = null;
        }
        if (this.typeValidator2) {
            this.criterionForm.controls.value2.controls.value.removeValidators(this.typeValidator2);
            this.typeValidator2 = null;
        }
        const dataTypeIsNumeric = this.dataTypeIsNumeric(this.getSelectedDatamartField()?.dataType);
        if (comparatorType === 'date') {
            this.typeValidator1 = typeValidator('date');
            this.criterionForm.controls.value1.controls.value.addValidators(this.typeValidator1);
        } else if (comparatorType === 'timeInterval') {
            this.typeValidator1 = typeValidator('numeric');
            this.criterionForm.controls.value1.controls.value.addValidators(this.typeValidator1);
        } else if (comparatorType === 'date&date') {
            this.typeValidator1 = typeValidator('date');
            this.criterionForm.controls.value1.controls.value.addValidators(this.typeValidator1);
            this.typeValidator2 = typeValidator('date');
            this.criterionForm.controls.value2.controls.value.addValidators(this.typeValidator2);
        } else if (comparatorType === 'text' && dataTypeIsNumeric) {
            this.typeValidator1 = typeValidator('numeric');
            this.criterionForm.controls.value1.controls.value.addValidators(this.typeValidator1);
        } else if (comparatorType === 'text&text' && dataTypeIsNumeric) {
            this.typeValidator1 = typeValidator('numeric');
            this.criterionForm.controls.value1.controls.value.addValidators(this.typeValidator1);
            this.typeValidator2 = typeValidator('numeric');
            this.criterionForm.controls.value2.controls.value.addValidators(this.typeValidator2);
        }

        this.criterionForm.controls.value1.controls.value.updateValueAndValidity();
        this.criterionForm.controls.value2.controls.value.updateValueAndValidity();
        this.criterionForm.updateValueAndValidity();
    }

    private disableAllControls() {
        this.criterionForm.controls.tableName.disable();
        this.criterionForm.controls.dataMartFieldName.disable();
        this.criterionForm.controls.not.disable();
        this.criterionForm.controls.comparator.disable();
        this.criterionForm.controls.value1.disable();
        this.criterionForm.controls.value2.disable();
        this.multipickFormControl.disable();
    }

    private populateMultipicklistControl() {
        const fieldName = this.criterionFormValue().dataMartFieldName;
        if (fieldName) {
            const selectedTable = this.tables()?.find(table => table.name === this.criterionFormValue().tableName);
            const matchingField = selectedTable?.fields.find(field => field.name === fieldName);
            if (matchingField?.fieldType.toLowerCase() === 'multipicklist') {
                // TODO - undo `!` cheese
                this.multipickFormControl.setValue(this.criterionFormValue().value1!.value!.split(','));
            }
        }
    }

    private setPicklistOptions() {
        const selectedTable = this.tables()?.find(table => table.name === this.criterionFormValue().tableName);
        const dataMartField = this.criterionFormValue().dataMartFieldName;
        if (selectedTable?.name && dataMartField) {
            this.#dataMartService.getPicklist(selectedTable.name, dataMartField).subscribe(res => {
                //we may get a 204 response that has no data but we should still blank out the picklist options
                this.picklistOptions = res ? res.entries : [];
            });
        }
    }

    onDateInput(event: MatDatepickerInputEvent<Date>, formControl: FormControl<string | null>) {
        formControl.setValue(event.value?.toISOString() ?? null);
    }

    onDelete() {
        this.deleteCriterion.emit(this.index());
    }

    onValueTypeahead(search: string) {
        const selectedTable = this.tables()?.find(table => table.name === this.criterionFormValue().tableName);
        const dataMartFieldName = this.criterionFormValue().dataMartFieldName;
        if (selectedTable?.name && dataMartFieldName) {
            this.#dataMartService.typeaheadSearch(selectedTable.name, dataMartFieldName, search).subscribe(results => {
                this.valueTypeaheadResults = results.entries.map(entry => {
                    return {
                        display: entry,
                        value: entry,
                    };
                });
            });
        }
    }

    onMultipickSelectChange() {
        this.criterionForm.controls.value1.controls.value.setValue(this.multipickFormControl.value.join(','));
    }

    onTableSelect(tableName: string, onLoad?: boolean) {
        this.fields.set(this.tables()?.find(table => table.name === tableName)?.fields ?? []);
        if (!onLoad) {
            this.criterionForm.controls.dataMartFieldName.reset();
            this.criterionForm.controls.not.reset();
            this.criterionForm.controls.comparator.reset();
            this.criterionForm.controls.value1.reset();
            this.criterionForm.controls.value2.reset();
        }
    }

    private dataTypeIsNumeric(dataType: string | undefined) {
        if (!dataType) return false;
        return ['integer', 'number', 'numeric'].includes(dataType.toLowerCase());
    }

    private getSelectedDatamartField() {
        const selectedDatamartFieldName = this.criterionFormValue().dataMartFieldName;
        if (!selectedDatamartFieldName) return undefined;
        return this.fields().find(f => f.name === selectedDatamartFieldName);
    }
}
